<script lang="ts">

  import LinkedRangeControls from './components/LinkedRangeControls.svelte';
  import MultiValueToggleControl from './components/MultiValueToggleControl.svelte';
  import type { LinkedRangeControlsPayload } from './components/types/linked-range-controls-payload';
  import { createEventDispatcher } from 'svelte';
  import type { FilterConstraints, FilterType, FilterValue } from './global';
  import { constraintsWritable } from './stores/config.store';

  export let enabledFilterControls: FilterType[] = [];
  export let styles = [];
  let constraints: FilterConstraints;
  let filterValue: FilterValue = {} as FilterValue;

  const defaultFilterValueFromConstraints: (c: FilterConstraints) => FilterValue = (c) => {
    let fv: FilterValue = {} as FilterValue;
    if (enabledFilterControls.includes('floor')) {
      fv.floor = {min: c.floorMin, max: c.floorMax};
    }
    if (enabledFilterControls.includes('floors')) {
      fv.floors = [];
    }
    if (enabledFilterControls.includes('roomsRange')) {
      fv.roomsRange = {min: c.roomsMin, max: c.roomsMax}
    }
    if (enabledFilterControls.includes('rooms')) {
      fv.rooms = [];
    }
    if (enabledFilterControls.includes('area')) {
      fv.area = {min: c.areaMin, max: c.areaMax};
    }
    if (enabledFilterControls.includes('price')) {
      fv.price = {min: c.priceMin, max: c.priceMax};
    }
    if (enabledFilterControls.includes('directions')) {
      fv.directions = [];
    }
    if (enabledFilterControls.includes('styles')) {
      fv.styles = [];
    }
    if (enabledFilterControls.includes('additionalAreas')) {
      fv.additionalAreas = [];
    }
    if (enabledFilterControls.includes('areas')) {
      fv.areas = [];
    }
    if (enabledFilterControls.includes('features')) {
      fv.features = [];
    }
    if(enabledFilterControls.includes('traits')) {
      fv.traits = [];
    }
    return fv;
  }

  constraintsWritable.subscribe(c => {
    constraints = c;
    filterValue = defaultFilterValueFromConstraints(c);
  })

  // region handlers
  const onFloorChange = (event: CustomEvent<LinkedRangeControlsPayload>) => {
    filterValue = {...filterValue, floor: {min: event.detail.minValue, max: event.detail.maxValue}};
  }

  const onRoomsRangeChange = (event: CustomEvent<LinkedRangeControlsPayload>) => {
    filterValue = {...filterValue, roomsRange: {min: event.detail.minValue, max: event.detail.maxValue}};
  }

  const onAreaChange = (event: CustomEvent<LinkedRangeControlsPayload>) => {
    filterValue = {...filterValue, area: {min: event.detail.minValue, max: event.detail.maxValue}};
  }

  const onPriceChange = (event: CustomEvent<LinkedRangeControlsPayload>) => {
    filterValue = {...filterValue, price: {min: event.detail.minValue, max: event.detail.maxValue}};
  }

  const onFloorsChange = (event: CustomEvent<number[]>) => {
    filterValue = {...filterValue, floors: event.detail};
  }

  const onDirectionsChange = (event: CustomEvent<('n' | 'e' | 's' | 'w')[]>) => {
    filterValue = {...filterValue, directions: event.detail};
  };

  const onRoomsChange = (event: CustomEvent<number[]>) => {
    filterValue = {...filterValue, rooms: event.detail}
  };

  const onAreasChange = (event: CustomEvent<number[]>) => {
    filterValue = { ...filterValue, areas: event.detail}
  }

  const onFeaturesChange = (event: CustomEvent<string[]>) => {
    filterValue = { ...filterValue, features: event.detail}
  }

  const onTraitsChange = (event: CustomEvent<string[]>) => {
    filterValue = { ...filterValue, traits: event.detail}
  }

  const onStylesChange = (event: CustomEvent<string[]>) => {
    filterValue = {...filterValue, styles: event.detail}
  };

  const onAdditionalAreasChange = (event: CustomEvent<('balcony' | 'terrace' | 'garden')[]>) => {
    filterValue = {...filterValue, additionalAreas: event.detail};
  }
  // endregion handlers

  const dispatch = createEventDispatcher<{ filterValue: FilterValue }>();
  const doFilter = () => {
    dispatch('filterValue', filterValue);
  }
</script>

{#if enabledFilterControls.includes('floor')}
  <div class="row">
    <div class="col-12 col-md-8">
      <LinkedRangeControls min={constraints.floorMin} max={constraints.floorMax} step={constraints.floorStep}
                           minValue={constraints.floorMin} maxValue={constraints.floorMax}
                           on:valueChange={onFloorChange}>
        <span slot="left">Piętro min</span>
        <span slot="right">Piętro max</span>
      </LinkedRangeControls>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('floors')}
  <div class="row">
    <div class="col-12 col-md-8">
      <MultiValueToggleControl values="{constraints.floors}"
                               name="floors"
                               on:valueChange={onFloorsChange}>
        Piętra
      </MultiValueToggleControl>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('roomsRange')}
  <div class="row">
    <div class="col-12 col-md-8">
      <LinkedRangeControls min={constraints.roomsMin} max={constraints.roomsMax} step={constraints.roomsStep}
                           minValue={constraints.roomsMin} maxValue={constraints.roomsMax}
                           on:valueChange={onRoomsRangeChange}>
        <span slot="left">Pomieszczenia min</span>
        <span slot="right">Pomieszczenia max</span>
      </LinkedRangeControls>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('rooms')}
  <div class="row">
    <div class="col-12 col-md-8">
      <MultiValueToggleControl values="{constraints.rooms}"
                               name="rooms"
                               on:valueChange={onRoomsChange}>
        Pomieszczenia
      </MultiValueToggleControl>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('areas')}
  <div class="row">
    <div class="col-12 col-md-8">
      <MultiValueToggleControl values="{constraints.areas}"
                               name="areas"
                               on:valueChange={onAreasChange}>
        Powierzchnia
      </MultiValueToggleControl>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('area')}
  <div class="row">
    <div class="col-12 col-md-8">
      <LinkedRangeControls min={constraints.areaMin} max={constraints.areaMax} step={constraints.areaStep} minDiff={1}
                           minValue={constraints.areaMin} maxValue={constraints.areaMax}
                           on:valueChange={onAreaChange}>
        <span slot="left">Powierzchnia min</span>
        <span slot="right">Powierzchnia max</span>
      </LinkedRangeControls>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('features')}
  <div class="row">
    <div class="col-12 col-md-8">
      <MultiValueToggleControl values="{constraints.features}"
                               name="features"
                               on:valueChange={onFeaturesChange}>
        Cechy
      </MultiValueToggleControl>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('traits')}
  <div class="row">
    <div class="col-12 col-md-8">
      <MultiValueToggleControl values="{constraints.traits}"
                               name="traits"
                               on:valueChange={onTraitsChange}>
        Atuty
      </MultiValueToggleControl>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('price')}
  <div class="row">
    <div class="col-12 col-md-8">
      <LinkedRangeControls min={constraints.priceMin}
                           max={constraints.priceMax}
                           step={constraints.priceStep}
                           minDiff={constraints.priceStep}
                           minValue={constraints.priceMin}
                           maxValue={constraints.priceMax}
                           on:valueChange={onPriceChange}>
        <span slot="left">Cena min</span>
        <span slot="right">Cena max</span>
      </LinkedRangeControls>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('directions')}
  <div class="row">
    <div class="col-12 col-md-8">
      <MultiValueToggleControl values="{constraints.directions}"
                               name="directions"
                               on:valueChange={onDirectionsChange}>
        Ekspozycja
      </MultiValueToggleControl>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('styles')}
  <div class="row">
    <div class="col-12 col-md-8">
      <MultiValueToggleControl values="{styles}"
                               name="styles"
                               on:valueChange={onStylesChange}>
        Styl
      </MultiValueToggleControl>
    </div>
  </div>
{/if}
{#if enabledFilterControls.includes('additionalAreas')}
  <div class="row">
    <div class="col-12 col-md-8">
      <MultiValueToggleControl values="{constraints.additionalAreas}"
                               name="additionalAreas"
                               on:valueChange={onAdditionalAreasChange}>
        Cechy
      </MultiValueToggleControl>
    </div>
  </div>
{/if}
<div class="row mt-3">
  <div class="col-12 col-md-4">
    <button class="btn btn-primary" type="button" on:click={doFilter}>Filtruj</button>
  </div>
</div>
